import { useState, useMemo, useCallback } from "react";
import "./ArticleHierarchyTreeView.css";

const defaultCanCheck = () => true;

const DEFAULT_HIERARCHY = [
    "recordType",
    "type",
    "name",
    "manufacturerShortName",
];

const buildTree = (articles, levels) => {
    const root = {};

    articles.forEach((article) => {
        let current = root;

        levels.forEach((level) => {
            const value = article[level] || "Unknown";

            current[value] ??= {
                children: {},
                articles: [],
            };

            current = current[value].children;
        });

        current.__articles ??= [];
        current.__articles.push(article);
    });

    return root;
};

const ArticleHierarchyTreeView = ({
    articles,
    hierarchy = DEFAULT_HIERARCHY,
    search,
    selectedArticle,
    onSelect,
    checkedIds,
    onCheckedIdsChange,
    canCheck = defaultCanCheck,
    checkboxDisabledTitle = "Not available",
    renderBadge,
}) => {
    const [collapsed, setCollapsed] = useState({});

    const toggleGroup = (key) => {
        setCollapsed((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();

        return articles.filter(
            (a) =>
                a.name?.toLowerCase().includes(q) ||
                a.manufacturer?.toLowerCase().includes(q) ||
                a.type?.toLowerCase().includes(q) ||
                a.articleNumber?.toLowerCase().includes(q)
        );
    }, [articles, search]);

    const tree = useMemo(
        () => buildTree(filtered, hierarchy),
        [filtered, hierarchy]
    );

    const toggleItem = useCallback(
        (id) => {
            const next = new Set(checkedIds);

            next.has(id) ? next.delete(id) : next.add(id);

            onCheckedIdsChange(next);
        },
        [checkedIds, onCheckedIdsChange]
    );

    const collectArticles = (node) => {
        let result = [];

        Object.entries(node).forEach(([key, value]) => {
            if (key === "__articles") {
                result.push(...value);
            } else {
                result.push(...collectArticles(value.children));
            }
        });

        return result;
    };

    const toggleGroupSelection = useCallback(
        (articlesInNode, checked) => {
            const next = new Set(checkedIds);

            articlesInNode.forEach((article) => {
                if (!canCheck(article)) return;

                checked
                    ? next.add(article.id)
                    : next.delete(article.id);
            });

            onCheckedIdsChange(next);
        },
        [checkedIds, onCheckedIdsChange, canCheck]
    );

    const getGroupState = (articlesInNode) => {
        const selectable = articlesInNode.filter((a) => canCheck(a));

        if (selectable.length === 0) return "disabled";

        const checkedCount = selectable.filter((a) =>
            checkedIds.has(a.id)
        ).length;

        if (checkedCount === 0) return "unchecked";
        if (checkedCount === selectable.length) return "checked";

        return "indeterminate";
    };

    const renderArticles = (articles) =>
        articles.map((article) => {
            const checkable = canCheck(article);

            return (
                <div
                    key={article.id}
                    className={`tree-item tree-leaf ${selectedArticle?.id === article.id ? "selected" : ""
                        }`}
                    onClick={() => onSelect(article)}
                >
                    <span className="tree-arrow-spacer" />

                    <input
                        type="checkbox"
                        className="tree-checkbox"
                        checked={checkedIds.has(article.id)}
                        disabled={!checkable}
                        title={!checkable ? checkboxDisabledTitle : undefined}
                        onChange={() => toggleItem(article.id)}
                        onClick={(e) => e.stopPropagation()}
                    />

                    <span className="tree-icon-wrap">
                        <span className="tree-icon tree-icon-item" />
                        {renderBadge?.(article)}
                    </span>

                    <span className="tree-label">{article.articleNumber}</span>
                </div>
            );
        });

    const renderNode = (node, path = [], level = 0) => {
        return Object.entries(node).map(([key, value]) => {
            if (key === "__articles") {
                return (
                    <div key={`${path.join("|")}-articles`}>
                        {renderArticles(value)}
                    </div>
                );
            }

            const nodeKey = [...path, key].join("|");

            const articlesInNode = collectArticles({
                value,
            });

            const groupState = getGroupState(articlesInNode);

            const isCollapsed = collapsed[nodeKey];

            return (
                <div key={nodeKey} className="tree-group">
                    <div
                        className={`tree-group-title ${isCollapsed ? "collapsed" : ""
                            }`}
                        style={{
                            paddingLeft: `${4 + level * 16}px`,
                        }}
                    >
                        <span
                            className="tree-arrow"
                            onClick={() => toggleGroup(nodeKey)}
                        />

                        <input
                            type="checkbox"
                            className="tree-checkbox"
                            checked={groupState === "checked"}
                            ref={(el) => {
                                if (el) {
                                    el.indeterminate =
                                        groupState === "indeterminate";
                                }
                            }}
                            disabled={groupState === "disabled"}
                            onChange={(e) =>
                                toggleGroupSelection(
                                    articlesInNode,
                                    e.target.checked
                                )
                            }
                            onClick={(e) => e.stopPropagation()}
                        />

                        <span className="tree-icon tree-icon-folder" />

                        <span
                            className="tree-label"
                            onClick={() => toggleGroup(nodeKey)}
                        >
                            {key}
                        </span>
                    </div>

                    {!isCollapsed && (
                        <div className="tree-children">
                            {renderNode(
                                value.children,
                                [...path, key],
                                level + 1
                            )}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="article-tree">
            {renderNode(tree)}
        </div>
    );
};

export default ArticleHierarchyTreeView;